import fetch from 'isomorphic-fetch';

import AWS from 'aws-sdk';

import { CognitoIdentityProviderClient, GetUserCommand } from '@aws-sdk/client-cognito-identity-provider';
import { CognitoUserPool, AuthenticationDetails, CognitoUser, CognitoRefreshToken } from 'amazon-cognito-identity-js';

import config from '../../config';
import storage from '../storage';

// import { USER_AUTH_TOKEN_ACCESS } from '../../modules/User/AuthActions';

let userPool = null;
let identityProvider = null;

if(process.env.NODE_ENV !== 'test') {
    userPool = new CognitoUserPool({
        UserPoolId: config.aws.cognito.userPoolId,
        ClientId: config.aws.cognito.clientId,
    });

    identityProvider = new CognitoIdentityProviderClient({
        region: config.aws.cognito.region,
    });
}

let keepUserLoggedInInterval = null;
let keepUserLoggedInIntervalTime = 1000; // ms

export function login(username, password) {
    const user = new CognitoUser({ Username: username, Pool: userPool });
    const authenticationData = { Username: username, Password: password };
    const authenticationDetails = new AuthenticationDetails(authenticationData);

    return new Promise((resolve, reject) => {
        user.authenticateUser(authenticationDetails, {
            onSuccess: result => {
                // console.log(result.getAccessToken().getJwtToken(), result.getIdToken().getJwtToken());
                refreshCredentials(result).then(() => {
                    resolve({ token: result.getAccessToken().getJwtToken() });
                }).catch(err => {
                    console.error(err);
                    reject(err);
                });
            },
            onFailure: err => reject(err),
            newPasswordRequired: (userAttributes, requiredAttributes) => {
                delete userAttributes.email_verified;
                resolve({ user, challenge: 'NEW_PASSWORD_REQUIRED', userAttributes });
            },
        });
    });
}

export function logWithRefreshToken(username, refreshTokenKey) {
    return new Promise((resolve, reject) => {
        const refreshToken = new CognitoRefreshToken({
            RefreshToken: refreshTokenKey,
        });

        const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
        cognitoUser.refreshSession(refreshToken, (err, session) => {
            if(err) {
                console.error('Cognito::: refreshToken ::: Error', err);
                reject(err);
            } else {
                // AWS.config.credentials.params.Logins[`cognito-idp.${config.aws.cognito.region}.amazonaws.com/${config.aws.cognito.userPoolId}`] = session.getIdToken().getJwtToken();
                AWS.config.region = config.aws.cognito.region;
                AWS.config.credentials = new AWS.CognitoIdentityCredentials({
                    IdentityPoolId: config.aws.cognito.identityPoolId,
                    [`cognito-idp.${config.aws.cognito.region}.amazonaws.com/${config.aws.cognito.userPoolId}`]: session.getIdToken().getJwtToken(),
                });
                AWS.config.credentials.refresh(err => {
                    if(err) {
                        console.error(err);
                        return reject(err);
                    }
                    console.log('Cognito::: refreshToken ::: Refreshed!');
                    getCredentials(session.getAccessToken().getJwtToken(), session.getAccessToken().getExpiration()).then(result => {
                        resolve(session.getAccessToken().getJwtToken());
                    }).catch(err => {
                        console.error(err);
                        reject(err);
                    });
                });
            }
        });
    });
}

export function refreshCredentials(session) {
    return new Promise((resolve, reject) => {
        if(session && session.getAccessToken()) {
            const token = session.getAccessToken().getJwtToken();
            const tokenExpiration = session.getAccessToken().getExpiration();

            getCredentials(token, tokenExpiration).then(result => {
                resolve(result.token);
            }).catch(err => {
                reject(err);
            });
        } else {
            reject(new Error('noSession'));
        }
    });
}

export function getCredentials(token, tokenExpiration) {
    return new Promise((resolve, reject) => {
        AWS.config.region = config.aws.cognito.region;
        AWS.config.credentials = new AWS.CognitoIdentityCredentials({
            IdentityPoolId: config.aws.cognito.identityPoolId,
            [`cognito-idp.${config.aws.cognito.region}.amazonaws.com/${config.aws.cognito.userPoolId}`]: token,
        });

        AWS.config.credentials.refresh(err => {
            if(err) {
                 console.error(err);
                 reject(err);
            } else {
                 // Instantiate aws sdk service objects now that the credentials have been updated.
                 // example: var s3 = new AWS.S3();
                 // console.log('Successfully logged!', AWS.config.credentials);
                //  if(storage) {
                //      console.info('Token has changed ?? => ', storage.getItem(USER_AUTH_TOKEN_ACCESS) !== token);
                //      storage.setItem(USER_AUTH_TOKEN_ACCESS, token);
                //  }
                 // console.log('Token expires on ', tokenExpiration);
                 keepUserLoggedInIntervalTime = Math.max(0, (tokenExpiration * 1000) - Date.now());
                 // console.log('Refresh token every ', keepUserLoggedInIntervalTime / 1000);

                 keepUserLoggedInIntervalTime && keepUserLoggedInInterval === null && keepUserLoggedIn();
                 resolve({ token });
            }
        });
    });
}

export function logout() {
    return getCurrentUser().then(currentUser => {
        if(currentUser) {
            return currentUser.signOut();
        }
        return false;
    }).catch(err => {
        console.error(err);
        return null;
    });
}

export function getCurrentUser() {
    return new Promise((resolve, reject) => {
        const currentUser = userPool.getCurrentUser();
        if(currentUser) {
            currentUser.getSession((err, session) => {
                if(err) {
                    console.error(err);
                    reject(err);
                }
                resolve(currentUser);
            });
        } else {
            reject(currentUser);
        }
    });
}

export function getUserByAccessToken(accessToken) {
    const command = new GetUserCommand({ AccessToken: accessToken });
    return identityProvider.send(command);
}

export function keepUserLoggedIn() {
    stopKeepLoggedIn();
    // keepUserLoggedInInterval = setInterval(refreshToken, keepUserLoggedInIntervalTime);
    keepUserLoggedInInterval = setTimeout(refreshToken, keepUserLoggedInIntervalTime);
}

export function stopKeepLoggedIn() {
    clearInterval(keepUserLoggedInInterval);
}

export function refreshToken(removeInterval = false) {
    removeInterval && stopKeepLoggedIn();
    return new Promise((resolve, reject) => {
        getCurrentUser().then(currentUser => {
            currentUser.getSession((err, session) => {
                if(err) {
                    console.error(err);
                    reject(err);
                }

                const refreshToken = session.getRefreshToken(); // receive session from calling cognitoUser.getSession()
                if(AWS.config.credentials && AWS.config.credentials.needsRefresh()) {
                    currentUser.refreshSession(refreshToken, (err, session) => {
                        if(err) {
                            console.error('Cognito::: refreshToken ::: Error', err);
                            reject(err);
                        } else {
                            // AWS.config.credentials.params.Logins[`cognito-idp.${config.aws.cognito.region}.amazonaws.com/${config.aws.cognito.userPoolId}`] = session.getIdToken().getJwtToken();
                            AWS.config.region = config.aws.cognito.region;
                            AWS.config.credentials = new AWS.CognitoIdentityCredentials({
                                IdentityPoolId: config.aws.cognito.identityPoolId,
                                [`cognito-idp.${config.aws.cognito.region}.amazonaws.com/${config.aws.cognito.userPoolId}`]: session.getIdToken().getJwtToken(),
                            });
                            AWS.config.credentials.refresh(err => {
                                if(err) {
                                    console.error(err);
                                    return reject(err);
                                }
                                console.log('Cognito::: refreshToken ::: Refreshed!');
                                getCredentials(session.getAccessToken().getJwtToken(), session.getAccessToken().getExpiration()).then(result => {
                                    resolve(session.getAccessToken().getJwtToken());
                                }).catch(err => {
                                    console.error(err);
                                    reject(err);
                                });
                            });
                        }
                    });
                } else {
                    console.log('Cognito::: refreshToken ::: No need to refresh');
                    // resolve();

                    refreshCredentials(session).then(result => {
                        resolve(session.getAccessToken().getJwtToken());
                    }).catch(err => {
                        console.error(err);
                        reject(err);
                    });
                }
            });
        }).catch(err => {
            console.error(err);
            reject(err);
        });
    });
}

export function getUserToken() {
    return new Promise((resolve, reject) => {
        getCurrentUser().then(currentUser => {
            currentUser.getSession((err, session) => {
                if(err) {
                    console.error(err);
                    reject(err);
                }
                resolve(session.getAccessToken().getJwtToken());
            });
        }).catch(err => {
            console.error(err);
            reject(err);
        });
    });
}

export function changePassword(oldPassword, newPassword) {
    return new Promise((resolve, reject) => {
        getCurrentUser().then(currentUser => {
            if(currentUser) {
                currentUser.changePassword(oldPassword, newPassword, (err, result) => {
                    if(err) {
                        console.error(err);
                        reject(err);
                        return;
                    }
                    resolve(result);
                });
            } else {
                reject(new Error('NoCognitoUser'));
            }
        });
    });
}

export function completeNewPasswordChallenge(user, userAttributes, newPassword) {
    return new Promise((resolve, reject) => {
        user.completeNewPasswordChallenge(newPassword, userAttributes, {
            onSuccess: result => resolve(result.getAccessToken().getJwtToken()),
            onFailure: err => reject(err),
        });
    });
}

export function forgotPassword(username) {
    return new Promise((resolve, reject) => {
        const user = new CognitoUser({ Username: username, Pool: userPool });
        user.forgotPassword({
            onSuccess: data => {
                // successfully initiated reset password request
                console.log('CodeDeliveryData from forgotPassword: ', data);
                resolve(data);
            },
            onFailure: err => {
                console.log(err);
                reject(err);
            },
        });
    });
}

export function confirmPassword(username, verificationCode, newPassword) {
    return new Promise((resolve, reject) => {
        // console.log(verificationCode, newPassword);
        const user = new CognitoUser({ Username: username, Pool: userPool });
        // console.log(user);
        user.confirmPassword(verificationCode, newPassword, {
            onSuccess() {
                resolve(true);
            },
            onFailure(err) {
                reject(err);
            },
        });
    });
}
